import React from 'react';
import { Form as AntdForm, FormInstance, Tooltip } from 'antd';
import { useLocation } from 'react-router';
import { FormItemProps } from 'antd/lib/form';
import styled from 'styled-components';
import cx from 'classnames';
import { toPath } from 'lodash';
import { useSelector } from 'react-redux';
import theme from '~/constants/theme';
import colors from '~/constants/colors';
import { handleDisablingByCopying } from '~/utils/contacts';
import { useFormItemContext } from './FormItemContext';
import { useSoftDeclineItemContext } from './useSoftDeclineItemContext';
import FloatLabel from './FloatLabel';
import { InputContextProvider } from './InputContext';
import { useSoftDeclineItemcardholder } from './useSoftDeclineItemcardholder';
import WizardForm from '../WizardForm';

interface Props<Values> extends FormItemProps<Values> {
  placeholder?: any;
  label?: string;
  optional?: boolean;
  children?: JSX.Element | RenderChildren;
  disabled?: boolean;
  textarea?: boolean;
  forceDisabled?: boolean;
  isFloatOpen?: boolean;
  adminType?: string;
  index?: any;
}

type FormItemType<Values = any> = (props: Props<Values>) => React.ReactElement;

function FormItem<Values = any>(props: Props<Values>): React.ReactElement {
  const {
    index,
    adminType,
    isFloatOpen,
    children,
    label,
    optional,
    rules = [],
    shouldUpdate,
    noStyle,
    disabled,
    className,
    textarea,
    forceDisabled,
    required,
    ...rest
  } = props;
  const { name }: any = rest;
  const { selectedContact } = useSelector((state: any) => state?.state);
  const disabledByCopying = handleDisablingByCopying({ selectedContact, name });
  const location = useLocation();
  const actualFormName = location.pathname;
  const isItcardholderFlow = actualFormName === '/cardHolder/details';
  const formItemContext = useFormItemContext();
  const softDeclineContext: any = useSoftDeclineItemContext(name, adminType, index);
  const { cardholderWarningMessage, cardHolderDisabled, isItcardholderSoftRejection, isValueUpdated }: any =
    useSoftDeclineItemcardholder(name);
  const contextOptional = !!Object.keys(formItemContext.optionalFields ?? {}).find((optionalNamePath) => {
    if (!name) return false;
    const resultOptionalNamePath = toPath(optionalNamePath).join('.');
    const resultItemNamePath = toPath([name].flat()).join('.');
    if (resultOptionalNamePath === resultItemNamePath) {
      return true;
    }
    return false;
  });
  const resultOptional = optional ?? contextOptional;

  const hasSoftDeclineWarning = !forceDisabled && softDeclineContext.withWarning;
  let resultClassName = cx(className, {
    FormItem__withWarning: hasSoftDeclineWarning || (cardholderWarningMessage && !isValueUpdated),
  });

  let resultDisabled =
    (!Object?.keys(selectedContact || {})?.length && forceDisabled) ||
    formItemContext.disabled ||
    (!softDeclineContext.editableBySoftDecline &&
      (formItemContext.disabledByDefault || softDeclineContext.disabled || disabled));
  // : disabledByCopying;

  let resultExtra = hasSoftDeclineWarning ? softDeclineContext.warningMessage : undefined;
  const defaultRule: typeof rules = formItemContext.defaultValidator
    ? [{ validator: formItemContext.defaultValidator }]
    : [];

  const doWeHaveAddresErr = formItemContext?.errorFields?.some((item) => {
    const [itemKey] = Object.keys(item || {});
    return itemKey?.includes('addresses');
  });
  const namePath = Array?.isArray(name) ? name?.join('.') : '';
  if (
    doWeHaveAddresErr &&
    (namePath === 'addresses.headOffice.buildingName/No' || namePath === 'addresses.headOffice.office/ShopNo')
  ) {
    resultDisabled = false;
    resultClassName = cx(className, {
      FormItem__withWarning: !softDeclineContext?.isValueUpdated,
    });
    resultExtra = !softDeclineContext?.isValueUpdated
      ? 'Please make sure this information corresponds to documents.'
      : '';
  }

  if (shouldUpdate) {
    return (
      <InputContextProvider value={{ optional: resultOptional }}>
        <AntdForm.Item shouldUpdate={shouldUpdate} noStyle>
          {(form) => (
            <AntdForm.Item
              rules={defaultRule.concat(rules)}
              noStyle={noStyle}
              extra={isItcardholderFlow ? cardholderWarningMessage : resultExtra}
              className={resultClassName}
              {...rest}
            >
              {typeof children === 'function' ? (children as any)?.(form) : children}
            </AntdForm.Item>
          )}
        </AntdForm.Item>
      </InputContextProvider>
    );
  }
  return (
    <InputContextProvider value={{ optional: resultOptional }}>
      <Tooltip
        placement="bottom"
        title={
          disabledByCopying
            ? `This field is filled by ${selectedContact?.salesForceUser ? 'existing user' : 'cardholder'}`
            : ''
        }
      >
        <AntdForm.Item
          rules={defaultRule.concat(rules)}
          noStyle={noStyle}
          className={resultClassName}
          extra={isItcardholderFlow ? (!isValueUpdated ? cardholderWarningMessage : '') : resultExtra}
          {...rest}
        >
          <FloatLabel
            isFloatOpen={isFloatOpen}
            label={label}
            optional={resultOptional}
            disabled={isItcardholderFlow ? disabled || forceDisabled || cardHolderDisabled : resultDisabled}
            required={required}
            textarea={textarea}
          >
            {children as any}
          </FloatLabel>
        </AntdForm.Item>
      </Tooltip>
    </InputContextProvider>
  );
}

export type RenderChildren<Values = any> = (form: FormInstance<Values>) => React.ReactNode;

export default styled(FormItem)`
  &.ant-form-item-with-help {
    margin-bottom: 0;
  }
  .ant-form-item-explain {
    font-size: 12px;
    min-height: ${theme.formVerticalSpacing}px !important;
    transition: 0.2s ease-in color;
    &:not(.ant-form-item-explain-error, .ant-form-item-explain-warning, .ant-form-item-explain-success) {
      color: ${colors.textColor};
    }
  }
  &.ant-form-item-has-warning {
    .ant-upload-select-picture-card {
      border-color: ${colors.warningColor} !important;
    }
    .ant-upload-list-picture-card .ant-upload-list-item {
      border: 1px solid ${colors.warningColor};
    }
  }
  &:not(.ant-form-item-has-error, .FormItem__withWarning) {
    .ant-input:focus,
    .ant-input-number:focus,
    .ant-input-number-focused,
    .ant-picker-focused {
      border-color: ${colors.inputHoverBorderColor};
      box-shadow: 0 0 0 2px ${colors.inputHoverBorderColor};
      padding: 5px;
    }
    .ant-select-focused .ant-select-selector {
      border-color: ${colors.inputHoverBorderColor} !important;
      box-shadow: 0 0 0 2px ${colors.inputHoverBorderColor} !important;
    }
  }
  &.FormItem__withWarning {
    // Regular input
    .ant-input:focus,
    .ant-input-number:focus,
    .ant-input-number-focused,
    .ant-picker-focused {
      border-color: ${colors.warningColor};
      box-shadow: 0 0 0 1px ${colors.warningColor};
    }
    input,
    .ant-input-number,
    .ant-select-selector,
    .ant-picker {
      border-color: ${colors.warningColor} !important;
    }
    .ant-select-focused .ant-select-selector {
      box-shadow: 0 0 0 1px ${colors.warningColor} !important;
    }
    // File input
    .ant-upload-select-picture-card,
    .DocumentInputContentMobile {
      border-color: ${colors.warningColor} !important;
    }
    .ant-upload-list-item-name {
      color: ${colors.warningColor};
    }
    .ant-upload-list-picture-card .ant-upload-list-item {
      border: 1px solid ${colors.warningColor};
    }
    .ant-form-item-extra {
      font-size: 12px;
      color: ${colors.warningColor};
    }
  }
` as FormItemType;
